import { applyPatch, deepClone, getValueByPointer, } from 'fast-json-patch';
import { ensureUid, hydrateSchema } from '../utils/SchemaUtils';
export function isObjectSchemaType(node) {
    return node?.type === 'object' && !!(node?.properties);
}
export function isArraySchemaType(node) {
    return node?.type === 'array' && !!(node?.items);
}
export var SchemaStoreErrors;
(function (SchemaStoreErrors) {
    SchemaStoreErrors["DuplicateProperty"] = "DUPLICATE_PROPERTY";
    SchemaStoreErrors["PropertyNotFound"] = "PROPERTY_NOT_FOUND";
})(SchemaStoreErrors || (SchemaStoreErrors = {}));
//#endregion
//#region SchemaTypeFactory
export const SchemaTypeFactory = {
    string: (description = '') => ensureUid({ type: 'string', description }),
    number: (description = '') => ensureUid({ type: 'number', description }),
    boolean: (description = '') => ensureUid({ type: 'boolean', description }),
    object: (props = {}, description = '', required = []) => ensureUid({
        type: 'object',
        additionalProperties: false,
        properties: props,
        description,
        required,
    }),
    array: (items = SchemaTypeFactory.string(), description = '') => ensureUid({
        type: 'array',
        description,
        items,
    }),
};
//#endregion
//#region Store Helpers
export function escapeJsonPointer(token) {
    return token.replace(/~/g, '~0').replace(/\//g, '~1');
}
function buildJsonPointer(path) {
    return '/' + path.map(escapeJsonPointer).join('/');
}
function skipRootPath(path) {
    return path === '' ? [] : path.split('/').slice(1);
}
function exists(doc, pointer) {
    try {
        if (pointer.endsWith('/')) {
            return false;
        }
        return !!getValueByPointer(doc, pointer);
    }
    catch {
        return false;
    }
}
function upsertPropertyOperation(doc, path, value) {
    if (!exists(doc, path)) {
        return {
            op: 'add',
            path,
            value,
        };
    }
    return {
        op: 'replace',
        path,
        value,
    };
}
//#endregion
//#region JsonSchemaStore
export class JsonSchemaStore {
    static $inject = ['initialState'];
    //#endregion
    //#region Constructor
    constructor(initialState) {
        this.listeners = new Set();
        const clonedState = deepClone(initialState);
        this.state = hydrateSchema(clonedState);
    }
    //#endregion
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    snapshot() {
        return this.state;
    }
    patch(patch) {
        const operations = Array.isArray(patch) ? patch : [patch];
        const clonedOperations = operations;
        const result = applyPatch(this.state, clonedOperations, true, true);
        this.state = result.newDocument;
        this.notifyListeners(clonedOperations);
        return this.snapshot();
    }
    upsertProperty(path, key, node) {
        const propertiesPath = buildJsonPointer([...skipRootPath(path), 'properties']);
        const properties = buildJsonPointer([...skipRootPath(path), 'properties', key]);
        const isRootPath = path === '';
        const operations = [];
        if (!exists(this.state, propertiesPath)) {
            operations.push({
                op: 'add',
                path: propertiesPath,
                value: {},
            });
        }
        if (isRootPath && !exists(this.state, '/type')) {
            operations.push({
                op: 'add',
                path: buildJsonPointer([...skipRootPath(path), 'type']),
                value: 'object',
            });
        }
        this.addKeyToRequired(isRootPath, path, key, operations);
        node = this.assignPositionAndUid(propertiesPath, node, properties);
        operations.push(upsertPropertyOperation(this.state, properties, node));
        return this.patch(operations);
    }
    remove(path) {
        if (path.endsWith('/')) {
            return this.patch({
                op: 'remove',
                path,
            });
        }
        if (!exists(this.state, path)) {
            return this.snapshot();
        }
        const parentPropertyPosition = path.lastIndexOf('/properties');
        if (parentPropertyPosition === -1) {
            console.warn('[JSON Schema store remove] cannot remove the path because the parent properties is not found', path);
            return this.snapshot();
        }
        const operations = [];
        const parentPath = path.substring(0, parentPropertyPosition);
        const key = path.substring(path.lastIndexOf('/') + 1);
        const parentNode = parentPath === '' ? this.state : getValueByPointer(this.state, parentPath);
        if (parentNode && parentNode.type === 'object' && Array.isArray(parentNode.required)) {
            const requiredPath = buildJsonPointer([...skipRootPath(parentPath), 'required']);
            if (parentNode.required.includes(key)) {
                const newRequired = parentNode.required.filter((k) => k !== key);
                operations.push({
                    op: 'replace',
                    path: requiredPath,
                    value: newRequired,
                });
            }
        }
        operations.push({
            op: 'remove',
            path,
        });
        return this.patch(operations);
    }
    setItems(path, items) {
        const itemsPath = buildJsonPointer([...skipRootPath(path), 'items']);
        const node = SchemaTypeFactory[items]();
        return this.patch(upsertPropertyOperation(this.state, itemsPath, node));
    }
    setDescription(path, description) {
        return this.patch([{
                op: 'replace',
                path: buildJsonPointer([...skipRootPath(path), 'description']),
                value: description,
            }]);
    }
    setNodeType(path, type) {
        const node = getValueByPointer(this.state, path);
        const plainNode = SchemaTypeFactory[type]();
        const replacedNode = {
            ...plainNode,
            description: node.description,
        };
        this.assignNodePosition(replacedNode, node.__position ?? 0);
        ensureUid(replacedNode, plainNode);
        return this.patch({
            op: 'replace',
            path: buildJsonPointer([...skipRootPath(path)]),
            value: replacedNode,
        });
    }
    setRequired(objectPath, key, isRequired = true) {
        const requiredPath = this.build(objectPath, 'required');
        const objectNode = (objectPath === '/' ? this.state : getValueByPointer(this.state, objectPath));
        if (objectNode?.type !== 'object') {
            return this.snapshot();
        }
        const required = objectNode?.required ?? [];
        const shouldChange = isRequired ? !required.includes(key) : required.includes(key);
        if (!shouldChange) {
            return this.snapshot();
        }
        const newRequired = isRequired ? required.concat([key]) : required.filter((k) => k !== key);
        return this.patch({
            op: exists(this.state, requiredPath) ? 'replace' : 'add',
            path: requiredPath,
            value: newRequired,
        });
    }
    renameProperty(objectPath, fromKey, toKey) {
        const from = this.build(objectPath, 'properties', fromKey);
        const path = this.build(objectPath, 'properties', toKey);
        if (exists(this.state, path)) {
            return { ...this.snapshot(), error: SchemaStoreErrors.DuplicateProperty };
        }
        this.patch({
            op: 'move',
            from,
            path,
        });
        const objectNode = (objectPath === '/' ? this.state : getValueByPointer(this.state, objectPath));
        if (objectNode.type !== 'object') {
            return this.snapshot();
        }
        const requiredPath = this.build(objectPath, 'required');
        const required = objectNode?.required ?? [];
        if (required.includes(fromKey)) {
            const newRequired = required.filter((key) => key !== fromKey).concat([toKey]);
            this.patch({
                op: exists(this.state, requiredPath) ? 'replace' : 'add',
                path: requiredPath,
                value: newRequired,
            });
        }
        return this.snapshot();
    }
    isRequired(objectPath, key) {
        const objectNode = (objectPath === '/' ? this.state : getValueByPointer(this.state, objectPath));
        if (objectNode?.type !== 'object') {
            return false;
        }
        const required = objectNode?.required ?? [];
        return required.includes(key);
    }
    //#region Private
    notifyListeners(ops) {
        const snapshot = this.snapshot();
        this.listeners.forEach((listener) => listener(snapshot, ops));
    }
    build(objectPath, ...tokens) {
        const base = objectPath === '/' ? '' : objectPath;
        return (base + '/' + tokens.map(escapeJsonPointer).join('/'));
    }
    receiveUidFromOldNode(path, node) {
        const oldNode = exists(this.state, path) ? getValueByPointer(this.state, path) : undefined;
        if (oldNode) {
            node = ensureUid(node, oldNode);
        }
        return node;
    }
    assignPositionAndUid(propertiesPath, node, properties) {
        const currentPosition = Object.keys(getValueByPointer(this.state, propertiesPath) || {}).length;
        this.assignNodePosition(node, currentPosition);
        node = this.receiveUidFromOldNode(properties, node);
        return node;
    }
    assignNodePosition(node, currentPosition) {
        Object.defineProperty(node, '__position', {
            value: currentPosition,
            enumerable: false,
            writable: true,
        });
    }
    addKeyToRequired(isRootPath, path, key, operations) {
        const parentNode = isRootPath ? this.state : getValueByPointer(this.state, path);
        if (parentNode?.type === 'object') {
            const requiredPath = buildJsonPointer([...skipRootPath(path), 'required']);
            const currentRequired = Array.isArray(parentNode.required) ? parentNode.required : [];
            const hasKeyInRequired = currentRequired.includes(key);
            if (hasKeyInRequired) {
                return;
            }
            const operation = !exists(this.state, requiredPath) ? 'add' : 'replace';
            operations.push({
                op: operation,
                path: requiredPath,
                value: currentRequired.concat(key),
            });
        }
    }
}
//#endregion
